import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../api/apiClient';

export default function SyncStatus({ sinceDate }) {
  const [status, setStatus] = useState(null);
  const [counts, setCounts] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [triggeredAt, setTriggeredAt] = useState(null);
  const [triggerError, setTriggerError] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get('/sync/status');
      setStatus(res.data.status || []);
      setCounts(res.data.counts || {});
      setLoadError(null);
    } catch (err) {
      console.error('[SyncStatus] Failed to load /sync/status:', err);
      setLoadError(err.response?.data?.error || err.message || 'Failed to load sync status');
    }
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [load]);

  // Sync runs in the background now (fire-and-forget), so poll faster right after a
  // manual trigger - otherwise the UI looks unchanged for up to 30s and seems stuck.
  useEffect(() => {
    if (!triggeredAt) return;
    const iv = setInterval(load, 3000);
    const timeout = setTimeout(() => setTriggeredAt(null), 5 * 60 * 1000);
    return () => { clearInterval(iv); clearTimeout(timeout); };
  }, [triggeredAt, load]);

  // Clear the "running" override once the background sync has actually finished
  useEffect(() => {
    if (!triggeredAt || !status) return;
    const finishedSinceTrigger = status.some(s => s.finished_at && new Date(s.finished_at + 'Z').getTime() > triggeredAt);
    const stillRunning = status.some(s => s.status === 'running');
    if (finishedSinceTrigger && !stillRunning) setTriggeredAt(null);
  }, [status, triggeredAt]);

  const triggerSync = async (full) => {
    setSyncing(true);
    setTriggerError(null);
    try {
      // Full sync is scoped to the currently selected date range instead of always
      // pulling the entire multi-year history - much faster, and matches what's on screen.
      await apiClient.post('/sync/trigger', full && sinceDate ? { sinceDate } : { full });
      setTriggeredAt(Date.now());
      await load();
    } catch (err) {
      setTriggerError(err.response?.data?.error || 'Failed to start sync');
      setTimeout(() => setTriggerError(null), 8000);
    } finally { setSyncing(false); }
  };

  if (!status) {
    return loadError ? (
      <div style={{ ...styles.bar, color: '#ef4444', fontSize: '0.75rem' }}>
        Sync status unavailable: {loadError}
      </div>
    ) : (
      <div style={{ ...styles.bar, color: '#94a3b8', fontSize: '0.75rem' }}>Loading sync status…</div>
    );
  }

  const lastSync = status.filter(s => s.status === 'done').sort((a, b) => b.finished_at?.localeCompare(a.finished_at))[0];
  const running = !!triggeredAt || status.some(s => s.status === 'running');
  const hasError = status.some(s => s.status === 'error');
  const ago = lastSync?.finished_at ? timeSince(lastSync.finished_at) : 'never';

  return (
    <div style={styles.bar}>
      <div style={styles.left}>
        <span style={{ ...styles.dot, background: running ? '#f59e0b' : hasError ? '#ef4444' : '#22c55e' }} />
        <span style={styles.label}>
          Data: {counts?.jobs?.toLocaleString() || 0} jobs, {counts?.invoices?.toLocaleString() || 0} invoices
        </span>
        <span style={styles.sep}>|</span>
        <span style={styles.ago}>{triggeredAt ? 'Sync running in background…' : `Last sync: ${ago}`}</span>
        {hasError && !triggeredAt && !triggerError && <span style={{ ...styles.ago, color: '#ef4444' }}>(sync error)</span>}
        {triggerError && <span style={{ ...styles.ago, color: '#ef4444' }}>{triggerError}</span>}
      </div>
      <div style={styles.right}>
        <button onClick={() => triggerSync(false)} disabled={syncing || running} style={styles.btn}>
          {running ? 'Syncing...' : 'Sync Now'}
        </button>
        <button onClick={() => triggerSync(true)} disabled={syncing || running} style={{ ...styles.btn, ...styles.btnFull }} title={sinceDate ? `Re-sync from ${sinceDate} through today` : 'Re-sync entire history'}>
          {sinceDate ? `Sync Since ${sinceDate}` : 'Full Sync'}
        </button>
      </div>
    </div>
  );
}

function timeSince(dateStr) {
  // Postgres timestamptz already comes back as a full ISO string (with Z or +offset) -
  // appending another Z produces an invalid date, which is why this showed "NaNd ago".
  const hasTz = /[Zz]$|[+-]\d\d:?\d\d$/.test(dateStr);
  const d = new Date(hasTz ? dateStr : dateStr + 'Z');
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (!Number.isFinite(s)) return 'unknown';
  if (s < 60) return 'just now';
  if (s < 3600) return Math.floor(s / 60) + 'm ago';
  if (s < 86400) return Math.floor(s / 3600) + 'h ago';
  return Math.floor(s / 86400) + 'd ago';
}

const styles = {
  bar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: '#fff', borderRadius: '0.75rem', padding: '0.5rem 1rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', flexWrap: 'wrap', gap: '0.5rem',
  },
  left: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
  right: { display: 'flex', gap: '0.375rem' },
  dot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  label: { fontSize: '0.75rem', fontWeight: 600, color: '#374151' },
  sep: { color: '#d1d5db', fontSize: '0.75rem' },
  ago: { fontSize: '0.75rem', color: '#94a3b8' },
  btn: {
    padding: '0.3rem 0.625rem', fontSize: '0.7rem', fontWeight: 600,
    border: '1px solid #e2e8f0', borderRadius: '0.375rem', cursor: 'pointer',
    background: '#f8fafc', color: '#475569',
  },
  btnFull: { background: '#fff', color: '#6366f1', borderColor: '#c7d2fe' },
};
